import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountingStandardApiModel } from '@assetaccountant/core/models/accounting-standard-api-model';
import { CURRENCIES } from '@assetaccountant/core/models/currencies';
import { EntityTypeApiModel } from '@assetaccountant/core/models/entity-type-api-model';
import { PeriodDepreciationCalculated } from '@assetaccountant/core/models/enum/period-depreciation-calculated';
import { Register } from '@assetaccountant/core/models/register';
import { RegisterReportingDateRangeWithDescription } from '@assetaccountant/core/models/register-reporting-date-range';
import { REPORTING_YEAR_DATES } from '@assetaccountant/core/models/reporting-year-dates';

import {
  RegisterData,
  RegisterParams,
  RegisterResolver,
} from '@assetaccountant/core/resolvers/register.resolver';
import { SubscriptionService } from '@assetaccountant/core/services/subscription.service';
import { TypedActivatedRoute, getChildRoute } from '@assetaccountant/core/utils/route-utils';
import { TypedFormBuilder, emptyValue } from '@assetaccountant/core/utils/typed-forms';
import { TypedNgbModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { AccountingStandardDto } from '@assetaccountant/api/models/accounting-standard.dto';
import { AssetAccountantSubscription } from '@assetaccountant/api/models/asset-accountant-subscription';
import { CountryDto } from '@assetaccountant/api/models/country.dto';
import { Feature } from '@assetaccountant/api/models/feature';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { RegisterApi } from '@assetaccountant/api/services/register.api';
import { ValidSubmitEvent } from '@assetaccountant/core/directives/forms/valid-submit.directive';
import { FeatureFlag } from '@assetaccountant/core/services/feature-flag';
import { LayoutService } from '@assetaccountant/core/services/layout.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';

@Component({
  selector: 'app-create-register-modal',
  templateUrl: './create-register-modal.component.html',
  styleUrls: ['./create-register-modal.component.scss'],
  standalone: false,
})
export class CreateRegisterModalComponent implements OnInit {
  readonly CURRENCIES = CURRENCIES;
  readonly FeatureFlag = FeatureFlag;
  readonly PackageFeature = Feature;

  @Input() initialName?: string;
  @Input() initialEntityName?: string;
  @Input() initialCountryId?: number;
  @Input() initialEntityTypeId?: number;

  @Output() onAccepted = new EventEmitter<Register>();

  @ViewChild('content') content: ElementRef;

  accountingStandards: AccountingStandardDto[] = [];
  canAddRegister = true;
  countries: CountryDto[] = [];
  country?: CountryDto;
  private countriesLoaded = false;
  private subscriptionLoaded = false;

  get contentLoaded(): boolean {
    return this.countriesLoaded && this.subscriptionLoaded;
  }
  defaultAccountingStandard?: AccountingStandardApiModel;
  reportingYearDates: RegisterReportingDateRangeWithDescription[] = [];
  entityTypes: EntityTypeApiModel[] = [];
  form = this.fb.group({
    id: 0,
    name: ['', [Validators.required, Validators.maxLength(50)]],
    entityName: [emptyValue<string>(), [Validators.required, Validators.maxLength(70)]],
    entityTypeId: [emptyValue<number>(), [Validators.required]],
    countryId: [emptyValue<number>(), Validators.required],
    currency: emptyValue<string>(),
    reportingYearDate: [
      emptyValue<RegisterReportingDateRangeWithDescription>(),
      Validators.required,
    ],
    accountsPeriodDepreciationCalculation: PeriodDepreciationCalculated.Daily_YearsAreEqual,
    accountingStandardId: emptyValue<number>(),
    depreciateBeyondResidual: false,
  });
  isFirstRegister: boolean;
  organisation: OrganisationDto;
  showAdvancedSettings = false;
  subscription: AssetAccountantSubscription;

  get hasChanges() {
    return this.form.dirty;
  }

  constructor(
    private fb: TypedFormBuilder,
    public modal: NgbActiveModal,
    @Inject(LOCALE_ID) public locale: string,
    private registerApi: RegisterApi,
    private router: Router,
    private organisationApi: OrganisationV2Api,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    private subscriptionService: SubscriptionService,
    private layoutService: LayoutService,
    private trackingService: TrackingService,
  ) {}

  ngOnInit() {
    this.reportingYearDates = REPORTING_YEAR_DATES;

    // default to 1st july
    const defaultReportingYearDate = this.getReportingYearDate(7, 1);
    this.form.controls.reportingYearDate.reset(defaultReportingYearDate);

    getChildRoute(this.route)
      .data.pipe(take(1))
      .subscribe(({ organisation }) => {
        this.organisation = organisation.activeOrganisation;
        this.registerApi.getCountries().subscribe(result => {
          this.countries = result;
          this.setDefaultCountry();
          this.countriesLoaded = true;
        });

        this.subscriptionService
          .getSubscription()
          .pipe(take(1))
          .subscribe(subscription => {
            this.isFirstRegister = !subscription.totalRegisters;
            this.subscription = subscription;
            this.canAddRegister = subscription.isManaged || !subscription.isRegisterLimitReached;

            const orgName = this.initialEntityName ?? organisation?.activeOrganisation?.name;

            if (this.initialName) {
              this.form.patchValue({ name: this.initialName, entityName: orgName });
            } else if (this.isFirstRegister) {
              this.form.patchValue({ name: orgName, entityName: orgName });
            } else {
              this.form.patchValue({ entityName: orgName });
            }

            this.subscriptionLoaded = true;
          });
      });
  }

  setDefaultCountry() {
    let defaultCountryId = this.initialCountryId ?? this.organisation?.billingCountryId;

    if (!defaultCountryId && this.countries && this.countries.length) {
      defaultCountryId = +this.countries[0].id;
    }

    if (defaultCountryId) {
      this.form.controls.countryId.setValue(defaultCountryId);
      this.changeCountry(defaultCountryId);
    }
  }

  changeCountry(selectedCountryId: number) {
    this.country = this.countries.find(c => c.id === +selectedCountryId);
    if (this.country) {
      const { entityTypes, defaultAccountingStandardId, accountingStandards } = this.country;

      this.entityTypes = entityTypes;
      this.defaultAccountingStandard = accountingStandards.find(
        a => a.id === defaultAccountingStandardId,
      );
      this.accountingStandards = accountingStandards;
      this.setDefaultEntityType();
      if (this.country.currency) {
        this.form.controls.currency.setValidators(null);
        this.form.controls.currency.updateValueAndValidity();
      } else {
        this.form.controls.currency.setValidators(Validators.required);
        this.form.controls.currency.updateValueAndValidity();
      }

      let reportingYearStartMonth = this.country.reportingYearStart?.month;

      if (reportingYearStartMonth > 12) {
        reportingYearStartMonth = 1;
      }
      const reportingYearStartDay = this.country.reportingYearStart?.day || 1;

      this.form.controls.reportingYearDate.reset(
        this.getReportingYearDate(reportingYearStartMonth, reportingYearStartDay),
      );
    }
  }

  setDefaultEntityType() {
    if (this.entityTypes && this.entityTypes.length) {
      const preferred = this.initialEntityTypeId
        ? this.entityTypes.find(t => t.id === this.initialEntityTypeId)
        : undefined;
      this.form.controls.entityTypeId.setValue((preferred ?? this.entityTypes[0]).id);
    }
  }

  onExitClick() {
    if (this.content.nativeElement && this.content.nativeElement.style) {
      this.content.nativeElement.style.display = 'none';
    }
  }

  save({ handle }: ValidSubmitEvent) {
    const reportingYearStartDateDay = this.form.controls.reportingYearDate.value.startDay;
    const reportingYearStartDateMonth = this.form.controls.reportingYearDate.value.startMonth;

    const command = {
      ...this.form.value,
      reportingYearStartDateDay,
      reportingYearStartDateMonth,
    };

    this.redirectAndCloseOnCompletion(
      handle(this.organisationApi.createRegister(command), { keepDisabledOnSuccess: true }),
    );
  }

  redirectAndCloseOnCompletion(response: Observable<{ register: RegisterDto }>) {
    return response.subscribe(async next => {
      RegisterResolver.clearCache();
      this.layoutService.triggerNavRefresh();

      this.trackingService.trackEvent(
        'Create register',
        this.subscription.totalRegisters.toString(),
      );

      const navigateCommand = [
        '/organisations/',
        next.register.organisationId,
        '/registers/',
        next.register.id,
        '/regimes/-/periods/-/assets',
      ].join('');

      // Removed until we handle explaining the user has a 30 day trial and doesn't have to choose a plan now
      // if (
      //   !this.organisation.isManaged &&
      //   this.isFirstRegister &&
      //   (!this.subscription.packageType || this.subscription.packageType === PackageType.Free)
      // ) {
      //   setupModal(this.ngbModal.open(ViewPricingModalComponent, { size: 'xl' }), modal => {
      //     modal.destinationRouteCommand = navigateCommand;
      //     modal.newOrganisation = true;
      //   });
      // } else {
      //   await this.router.navigateByUrl(navigateCommand);
      // }

      await this.router.navigateByUrl(navigateCommand);

      this.subscriptionService.reset();
      this.modal.close();
      this.formReset();
    });
  }

  getReportingYearDate = (
    startMonth: number,
    startDay: number,
  ): RegisterReportingDateRangeWithDescription | undefined => {
    const selectedReportingYearDate = this.reportingYearDates.find(
      d => d.startDay === startDay && d.startMonth === startMonth,
    );
    return selectedReportingYearDate;
  };

  private formReset() {
    this.form.controls.name.setValue('');
    this.form.controls.name.markAsUntouched();
    this.form.controls.reportingYearDate.reset(this.getReportingYearDate(7, 1));
  }
}
